import { useEffect, useReducer, useCallback } from 'react'
import { get } from 'lodash'
import numeral from 'numeral'
import api, { Order, ICostBuilding } from '@api'
import { createYearOptions } from '@ui/selects/YearSelect'

enum Actions {
  FILTER_CITY,
  LOAD,
  LOAD_TOTALS,
  LOAD_START,
  SET_YEAR,
  SET_BUILDING,
  SET_EXPORT,
  SET_SORT,
  UPDATE_COST,
}

interface IState {
  buildings: { [id: string]: ICostBuilding }
  exportList: string
  isLoading: boolean
  sort: {
    column: ''
    order: Order.ASC
  }
  year: number
  yearOptions: number[]
  annualTotal: number
  annualLease: number
  annualOps: number
  annualCostPerSqft: number
  annualCostPerSeat: number
  annualCostPerEmployee: number
  filters: {
    city: string
  }
}

export interface ICost extends IState {
  filterCity: (city: string) => void
  setYear: (year: number) => void
  updateCost: (cost: any) => void
  setBuilding: (building: ICostBuilding) => void
  download: () => void
  reload: () => void
  setSort: (column: string) => void
}

const calculateTotals = (buildings: ICostBuilding[]) => {
  const reduce = (field: string, useNumeral?: boolean) =>
    buildings.reduce((acc: number, b: ICostBuilding) => {
      let val = get(b, ['costs', field])
      if (useNumeral) val = numeral(val).value()
      return acc + val
    }, 0)
  const annualTotal = reduce('annualTotal')
  const totals: { [i: string]: number } = {
    annualLease: reduce('annualLease'),
    annualTotal,
    annualOps: reduce('annualOps', true),
    annualCostPerSqft: annualTotal / reduce('sqft'),
    annualCostPerSeat: annualTotal / reduce('spacesCount'),
    annualCostPerEmployee: annualTotal / reduce('employeesCount'),
  }
  for (const [k, v] of Object.entries(totals)) if (v === Infinity) totals[k] = 0
  return totals
}

function reducer(state: any, action: any) {
  switch (action.type) {
    case Actions.LOAD:
      return {
        ...state,
        buildings: action.buildings.reduce(
          (map: { [id: string]: ICostBuilding }, b: ICostBuilding) => {
            map[b.id] = b
            return map
          },
          {}
        ),
        ...calculateTotals(action.buildings),
        isLoading: false,
      }
    case Actions.SET_SORT:
      return {
        ...state,
        sort: {
          ...state.sort,
          column: action.column,
          order:
            action.column === state.sort.column
              ? state.sort.order === Order.DESC
                ? Order.ASC
                : Order.DESC
              : Order.DESC,
        },
      }
    case Actions.LOAD_START:
      return {
        ...state,
        isLoading: true,
      }
    case Actions.SET_YEAR:
      return {
        ...state,
        year: action.year,
      }
    case Actions.FILTER_CITY:
      if (action.city === 'All Cities') action.city = ''
      return {
        ...state,
        filters: {
          ...state.filters,
          city: action.city,
        },
      }
    case Actions.UPDATE_COST:
      return {
        ...state,
        buildings: {
          ...state.buildings,
          [action.id]: {
            ...state.buildings[action.id],
            costs: {
              ...state.buildings[action.id].costs,
              ...action.cost,
            },
          },
        },
      }
    case Actions.SET_BUILDING:
      const buildings = {
        ...state.buildings,
        [action.building.id]: action.building,
      }
      return {
        ...state,
        buildings,
        ...calculateTotals(Object.values(buildings)),
      }
    case Actions.SET_EXPORT:
      return {
        ...state,
        exportList: action.exportList,
      }
    default:
      return state
  }
}

export default function useCost(year: number): ICost {
  const [data, dispatch] = useReducer(reducer, {
    buildings: {},
    exportList: '',
    sort: 'costs.annualTotal',
    year,
    isLoading: true,
    filters: {
      city: '',
    },
    yearOptions: createYearOptions(2000, 50),
  })

  const load = useCallback(() => {
    dispatch({ type: Actions.LOAD_START })
    api.cost.byCity({ year: data.year, city: data.filters.city }).then(({ buildings }) => {
      // an expired building will have {costs: null}
      const buildingsWithCosts = buildings.filter((b) => b.costs !== null)
      dispatch({ type: Actions.LOAD, buildings: buildingsWithCosts })
    })
  }, [data.filters.city, data.year])

  useEffect(load, [data.year, data.filters.city])

  const setYear = useCallback((year: number) => dispatch({ type: Actions.SET_YEAR, year }), [])

  return {
    ...data,
    filterCity: (city: string) => dispatch({ type: Actions.FILTER_CITY, city }),
    setYear,
    updateCost: ({ id, cost }: { id: string; cost: any }) =>
      dispatch({ type: Actions.UPDATE_COST, id, cost }),
    setBuilding: (building: ICostBuilding) => dispatch({ type: Actions.SET_BUILDING, building }),
    download: () => {
      const cities: string[] = [data.filters.city].filter((v) => v)
      api.cost
        .download({
          year: data.year,
          cities,
        })
        .then((exportList: string) => dispatch({ type: Actions.SET_EXPORT, exportList }))
    },
    reload: load,
    setSort: (column: string) => dispatch({ type: Actions.SET_SORT, column }),
  }
}
